/**
 * Resolves the child family member ID for a placeholder when adding a parent.
 * Placeholder IDs have the form: placeholder-{anchorOrParentId}-{position}
 * where position is left-root, right-root, left-parent, right-parent,
 * left-grandparent-left, left-grandparent-right, right-grandparent-left, right-grandparent-right.
 */
export function getChildIdFromPlaceholderId(
  placeholderId: string,
  context: {
    treeAnchorFamilyMemberId: string;
    leftParentFamilyMemberId: string | null;
    rightParentFamilyMemberId: string | null;
  }
): string | null {
  if (!placeholderId.startsWith('placeholder-')) {
    return null;
  }
  const suffix = placeholderId.slice('placeholder-'.length);
  // Position suffixes: left-root, right-root, left-parent, right-parent,
  // left-grandparent-left, left-grandparent-right, right-grandparent-left, right-grandparent-right
  if (suffix.endsWith('-left-parent')) {
    const treeAnchorId = suffix.slice(0, -'-left-parent'.length);
    return treeAnchorId || context.treeAnchorFamilyMemberId;
  }
  if (suffix.endsWith('-right-parent')) {
    const treeAnchorId = suffix.slice(0, -'-right-parent'.length);
    return treeAnchorId || context.treeAnchorFamilyMemberId;
  }
  if (suffix.endsWith('-left-grandparent-left') || suffix.endsWith('-left-grandparent-right')) {
    return context.leftParentFamilyMemberId;
  }
  if (suffix.endsWith('-right-grandparent-left') || suffix.endsWith('-right-grandparent-right')) {
    return context.rightParentFamilyMemberId;
  }
  return null;
}

/**
 * Returns true if the placeholder is a parent or grandparent (not root spouse).
 * Root placeholders are for "add spouse"; parent/grandparent placeholders are for "add parent".
 */
export function isParentOrGrandparentPlaceholder(placeholderId: string): boolean {
  if (!placeholderId.startsWith('placeholder-')) {
    return false;
  }
  const suffix = placeholderId.slice('placeholder-'.length);
  return (
    suffix.endsWith('-left-parent') ||
    suffix.endsWith('-right-parent') ||
    suffix.endsWith('-left-grandparent-left') ||
    suffix.endsWith('-left-grandparent-right') ||
    suffix.endsWith('-right-grandparent-left') ||
    suffix.endsWith('-right-grandparent-right')
  );
}
